import { useContext, useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../config/firebase";
import { SchoolContext } from "../config/SchoolContext";
import { useAuth } from "../config/AuthContext";

// Single source of truth for whether the logged-in user has Course Planning
// beta access. Access is granted per-school (CcpBetaTesting/{CcpSchools doc
// id}.features.coursePlanning) OR per-district
// (CcpDistrictBetaTesting/{district name}.features.coursePlanning, matched
// against currentUser.district — the same field the district dashboard
// already uses to look up a district's schools). Either check enabling the
// feature is enough; both are managed from the admin Beta Testing page.
const useCoursePlanningBetaAccess = () => {
  const { schoolId } = useContext(SchoolContext);
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const checkSchool = async () => {
      const sid = schoolId || currentUser?.school_id;
      if (!sid) return false;

      // CcpSchools.school_id is stored inconsistently across documents —
      // sometimes a Number, sometimes a String (confirmed live: Aiken's
      // CcpSchools doc has school_id as Number 150, while every Aiken
      // users doc has school_id as String "150"). Firestore's `==` is
      // type-strict, so check both representations via `in`.
      const numericSid = Number(sid);
      const sidCandidates = Number.isNaN(numericSid) ? [sid] : [sid, numericSid];

      const schoolSnap = await getDocs(
        query(
          collection(db, "CcpSchools"),
          where("school_id", "in", sidCandidates),
        ),
      );
      if (schoolSnap.empty) return false;

      const schoolDocId = schoolSnap.docs[0].id;
      const betaSnap = await getDoc(doc(db, "CcpBetaTesting", schoolDocId));
      return !!betaSnap.data()?.features?.coursePlanning;
    };

    const checkDistrict = async () => {
      const district = currentUser?.district;
      if (!district) return false;

      const betaSnap = await getDoc(doc(db, "CcpDistrictBetaTesting", district));
      return !!betaSnap.data()?.features?.coursePlanning;
    };

    const check = async () => {
      setLoading(true);
      try {
        const [schoolEnabled, districtEnabled] = await Promise.all([
          checkSchool(),
          checkDistrict(),
        ]);

        if (!cancelled) {
          setEnabled(schoolEnabled || districtEnabled);
          setLoading(false);
        }
      } catch (err) {
        console.error("Error checking Course Planning beta access:", err);
        if (!cancelled) {
          setEnabled(false);
          setLoading(false);
        }
      }
    };

    check();
    return () => {
      cancelled = true;
    };
  }, [schoolId, currentUser]);

  return { enabled, loading };
};

export default useCoursePlanningBetaAccess;
